import { ReactFlow, Background, BackgroundVariant, type NodeTypes, type EdgeTypes } from "@xyflow/react"
import "@xyflow/react/dist/style.css"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ModuleNode } from "@/components/project/nodes/ModuleNode"
import { TypedEdge } from "@/components/project/edges/TypedEdge"
import type { Project } from "@/lib/api/projects"
import type { PipelineNode, PipelineEdge } from "@/lib/types/pipeline"

const nodeTypes: NodeTypes = { module: ModuleNode }
const edgeTypes: EdgeTypes = { typed: TypedEdge }

interface TemplatePreviewDialogProps {
  template: Project | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUseTemplate: (templateId: string) => void
  loading?: boolean
}

export function TemplatePreviewDialog({
  template,
  open,
  onOpenChange,
  onUseTemplate,
  loading = false,
}: TemplatePreviewDialogProps) {
  const nodes = (template?.graph?.nodes as PipelineNode[]) ?? []
  const edges = (template?.graph?.edges as PipelineEdge[]) ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-3xl bg-surface-raised border-white/10 p-0 gap-0 overflow-hidden"
        showCloseButton
      >
        <DialogHeader className="px-6 pt-5 pb-4">
          <div className="flex items-center justify-between pr-8">
            <div>
              <DialogTitle className="text-lg">{template?.name ?? "Loading..."}</DialogTitle>
              {template?.description && (
                <DialogDescription className="mt-1">
                  {template.description}
                </DialogDescription>
              )}
            </div>
            <Button
              onClick={() => template && onUseTemplate(template.id)}
              disabled={loading || !template}
              size="sm"
            >
              {loading ? "Creating..." : "Use template"}
            </Button>
          </div>
        </DialogHeader>

        <div className="h-80 w-full border-t border-border/50 bg-muted dark:bg-zinc-1000">
          {!template ? (
            <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
              Loading...
            </div>
          ) : nodes.length > 0 ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultEdgeOptions={{ type: "typed" }}
              fitView
              fitViewOptions={{ padding: 0.3 }}
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              nodesFocusable={false}
              edgesFocusable={false}
              elementsSelectable={false}
              panOnDrag={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              zoomOnDoubleClick={false}
              preventScrolling={false}
            >
              <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            </ReactFlow>
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
              Empty template
            </div>
          )}
        </div>

        <DialogFooter className="bg-transparent border-t border-border/50 -mx-0 -mb-0 rounded-b-xl px-6 py-3">
          <p className="text-muted-foreground text-xs mr-auto">
            {nodes.length} {nodes.length === 1 ? "node" : "nodes"} &middot; {edges.length} {edges.length === 1 ? "connection" : "connections"}
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
